<template>
  <div>
    <div class="modal" :class="{ 'is-active': value }">
      <div class="modal-background" @click="() => {}"></div>
      <div class="modal-content" style="max-width: 600px">
        <div class="box modal-container">
          <div class="modal-content-root" v-if="task">
            <div class="d-flex align-center">
              <div class="task-modal-title-text flex-grow-1">
                {{ getTaskSignalDisplayName(task) }}
              </div>
              <div>
                <i
                  class="material-icons box-dot-menu"
                  :id="`show-task-modal-dot-${task.checklistId}`"
                  >more_horiz</i
                >
                <v-menu
                  :activator="`#show-task-modal-dot-${task.checklistId}`"
                  bottom
                  offset-y
                >
                  <v-list>
                    <v-list-item link @click="showConfirmDeleteTaskModal = true">
                      <v-list-item-title class="mainColor--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <div class="mt-5">
              <table class="summary-table">
                <tbody>
                  <tr>
                    <td>Assignee</td>
                    <td colspan="4">
                      <div class="d-flex align-center">
                        <div
                          class="avatar-circle"
                          :style="{
                            background: strToHslColor(currentUserEmail),
                          }"
                        >
                          <template v-if="currentUserEmail">
                            {{ currentUserEmail[0].toUpperCase() }}
                          </template>
                          <template v-else>
                            <span class="material-icons">person</span>
                          </template>
                        </div>
                        <span class="ml-2 font-weight-bold">
                          {{ currentUserEmail }}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>Due date</td>
                    <td colspan="4" class="font-weight-bold">
                      {{ toLocalDateString(task.dueDate) }}
                    </td>
                  </tr>
                  <tr>
                    <td>Status</td>
                    <td colspan="4">
                      <task-status-readonly
                        :task="task"
                        @statusChanged="$emit('statusChanged')"
                      ></task-status-readonly>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              class="mt-5"
              v-if="task.type === ALL_TYPES.WATCHDOG_TASK
                && (task.customRecommendationText || '').trim()"
            >
              <div class="mb-2 notes-label">Description</div>
              <div
                class="task-description"
                v-html="task.customRecommendationText.replace(/\n/g, '<br/>')"
              ></div>
            </div>
            <div class="mt-5">
              <div class="mb-2 notes-label">Notes</div>
              <v-textarea
                outlined
                hide-details
                color="greenColor"
                rows="3"
                :rules="[(v) => v.length <= 80]"
                label="Add your note here"
                v-model="newNote"
              ></v-textarea>
              <div v-if="isNoteEditMode" class="mt-2">
                <v-btn
                  rounded
                  outlined
                  color="mainColor"
                  class="button-with-icon text-none"
                  @click="updateNote"
                  :disabled="newNote.trim() == ''"
                >
                  <span>Update note</span>
                </v-btn>
                <v-btn
                  rounded
                  outlined
                  color="grayColor"
                  class="button-with-icon text-none ml-2"
                  @click="cancelEditNoteMode"
                >
                  Cancel
                </v-btn>
              </div>
              <div v-else class="mt-2">
                <v-btn
                  rounded
                  outlined
                  color="mainColor"
                  class="button-with-icon text-none"
                  @click="addNote"
                  :disabled="newNote.trim() == ''"
                >
                  Add note
                </v-btn>
              </div>
            </div>
            <div class="mt-7">
              <article
                v-for="row in currentTaskUserNotes"
                :key="`user_note_${row.id}`"
                class="user-note-item"
              >
                <div class="note-header d-flex align-center">
                  <div class="flex-grow-1">
                    <div class="who">{{ row.email }}</div>
                    <div class="when mt-1">
                      {{ row.createdAt }}
                      <span v-if="row.isEdited" class="is-italic is-size-7">
                        ( edited: {{ row.editedAt }} )</span
                      >
                    </div>
                  </div>
                  <div>
                    <button
                      class="edit-note-button"
                      aria-label="edit"
                      @click="setEditNoteMode(row)"
                      v-if="!row.isAutoNote"
                    >
                      Edit
                    </button>
                    <button
                      class="delete-note-button"
                      aria-label="delete"
                      @click="deleteNote(row.id)"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div
                  class="note-body"
                  v-html="row.note.replace(/\n/g, '<br/>')"
                ></div>
              </article>
              <div
                v-for="note in currentTaskAutoNotes"
                :key="`auto_note_${note.id}`"
                class="auto-note-item"
              >
                <div
                  class="avatar-circle"
                  :style="{ background: strToHslColor(note.email) }"
                >
                  <template v-if="note.email">
                    {{ note.email[0].toUpperCase() }}
                  </template>
                  <template v-else>
                    <span class="material-icons">person</span>
                  </template>
                </div>
                <div class="who">
                  {{ note.email ? note.email : "" }}
                </div>
                <div class="note-text">{{ note.note }}</div>
                <div class="date">
                  {{ toLocalDateString(note.createdAt) }}
                </div>
              </div>
            </div>

            <div class="text-right mt-5 pr-2">
              <v-btn
                rounded
                color="grayColor"
                class="button-with-icon mainColor--text text-none"
                @click="close"
              >
                <span>Close</span>
              </v-btn>
            </div>
          </div>
        </div>
        <button
          class="custom-modal-close-btn"
          aria-label="close"
          @click="close"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>

    <div
      class="modal"
      :class="{ 'is-active': showConfirmDeleteTaskModal }"
    >
      <div class="modal-background" @click="() => {}"></div>
      <div class="modal-content">
        <div class="box modal-container">
          <div class="modal-content-root">
            <div class="is-size-4 font-weight-bold mainColor--text">
              Delete this task?
            </div>
            <div class="mt-5 mainColor--text">
              Deleted task will be kept in your recycle bin for 30 days.
            </div>
            <div class="text-right mt-5">
              <v-btn
                rounded
                outlined
                color="mainColor"
                class="button-with-icon text-none"
                @click="showConfirmDeleteTaskModal = false"
              >
                <span>Cancel</span>
              </v-btn>
              <v-btn
                rounded
                color="profileAnchorColor"
                class="button-with-icon whiteColor--text text-none ml-2"
                @click="deleteTaskAction"
              >
                <span>Delete</span>
              </v-btn>
            </div>
          </div>
        </div>
        <button
          class="custom-modal-close-btn"
          aria-label="close"
          @click="showConfirmDeleteTaskModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin } from "./common_mixin.js";
import { toLocalDateString, strToHslColor } from "@/utils";
import TaskStatusReadonly from "./ProgressNotes/TaskStatusReadonly.vue";

export default {
  name: "TaskDetailModal",
  mixins: [mixin],
  components: { TaskStatusReadonly },
  props: {
    value: { type: Boolean, default: false },
    task: { type: Object, default: null },
    currentUserEmail: { type: String, default: "" },
    permanentDelete: { type: Boolean, default: false },
  },
  data() {
    return {
      newNote: "",
      isNoteEditMode: false,
      selectedNoteId: "",
      showConfirmDeleteTaskModal: false,
    };
  },
  computed: {
    currentTaskUserNotes() {
      if (!this.task) return [];
      return this.task.notes.filter((n) => !n.isAutoNote);
    },
    currentTaskAutoNotes() {
      if (!this.task) return [];
      return this.task.notes.filter((n) => n.isAutoNote);
    },
  },
  watch: {
    value(open) {
      if (!open) {
        this.cancelEditNoteMode();
        this.showConfirmDeleteTaskModal = false;
      }
    },
    task() {
      this.cancelEditNoteMode();
    },
  },
  methods: {
    toLocalDateString,
    strToHslColor,
    close() {
      this.$emit("input", false);
    },
    addNote() {
      if (!this.task || !this.newNote.trim()) return;
      this.$store.commit("showLoading");
      return this.$store
        .dispatch("createChecklistNote", {
          checklistId: this.task.checklistId,
          note: this.newNote,
        })
        .then((response) => {
          if (response.data && response.data.status === "SUCCESS") {
            this.newNote = "";
            this.$emit("tasksChanged");
          }
        })
        .finally(() => this.$store.commit("hideLoading"));
    },
    updateNote() {
      if (!this.selectedNoteId || !this.newNote.trim()) return;
      this.$store.commit("showLoading");
      return this.$store
        .dispatch("updateChecklistNote", {
          id: this.selectedNoteId,
          note: this.newNote,
        })
        .then((response) => {
          if (response.data && response.data.status === "SUCCESS") {
            this.cancelEditNoteMode();
            this.$emit("tasksChanged");
          }
        })
        .finally(() => this.$store.commit("hideLoading"));
    },
    setEditNoteMode(note) {
      this.isNoteEditMode = true;
      this.selectedNoteId = note.id;
      this.newNote = note.note;
    },
    cancelEditNoteMode() {
      this.isNoteEditMode = false;
      this.selectedNoteId = 0;
      this.newNote = "";
    },
    deleteNote(noteId) {
      if (!window.confirm("Are you sure you want to delete this note?")) {
        return;
      }
      this.$store.commit("showLoading");
      return this.$store
        .dispatch("deleteChecklistNote", noteId)
        .then((response) => {
          if (response.data && response.data.status === "SUCCESS") {
            this.$emit("tasksChanged");
          }
        })
        .finally(() => this.$store.commit("hideLoading"));
    },
    deleteTaskAction() {
      if (!this.task) return;
      const data = {
        id: this.task.checklistId,
        isPermanentDelete: this.permanentDelete ? 1 : 0,
      };
      this.$store.commit("showLoading");
      return this.$store
        .dispatch("archiveOneFromCheckList", data)
        .then((response) => {
          this.showConfirmDeleteTaskModal = false;
          this.close();
          if (response.data && response.data.status === "SUCCESS") {
            this.$emit("deleted");
          }
        })
        .finally(() => this.$store.commit("hideLoading"));
    },
  },
};
</script>

<style lang="scss" scoped>
.task-modal-title-text {
  font-size: 1.5rem;
  line-height: 1.8rem;
  font-weight: 800;
  color: var(--v-mainColor-base);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.summary-table {
  td {
    font-size: 1.1rem;
    color: var(--v-mainColor-base);
    vertical-align: middle;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  tr > td:first-child {
    padding-right: 30px;
  }
}
.notes-label {
  font-size: 1.1rem;
  color: var(--v-mainColor-base);
}
.task-description {
  font-size: 0.95rem;
  color: #424242;
  white-space: pre-wrap;
  line-height: 1.5;
}
.user-note-item {
  border-radius: 7px;
  border: 1px solid var(--v-grayColor-base);
  overflow: hidden;
  margin-top: 15px;
  .note-header {
    background: var(--v-lightYellowColor-base);
    padding: 10px 10px;
    border-bottom: 1px solid var(--v-grayColor-base);
    .who {
      color: var(--v-mainColor-base);
      font-weight: 700;
      font-size: 0.9rem;
    }
    .when {
      font-size: 0.7rem;
      color: var(--v-mainColor-base);
    }
  }
  .note-body {
    padding: 15px 10px;
    color: var(--v-mainColor-base);
    font-size: 0.95rem;
  }
}
.avatar-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 0.85rem;
  color: var(--v-whiteColor-base);
  display: flex;
  align-items: center;
  justify-content: center;
}
.auto-note-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 25px;
  .who {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
  }
  .note-text {
    color: var(--v-mainColor-base);
  }
  .date {
    position: relative;
    padding-left: 10px;
    &::before {
      position: absolute;
      left: 0px;
      top: 50%;
      width: 5px;
      height: 5px;
      border-radius: 5px;
      background: var(--v-mainColor-base);
      transform: translateY(-50%);
      content: "";
    }
    color: var(--v-mainColor-base);
  }
}
.edit-note-button {
  color: var(--v-mainColor-base);
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--v-whiteColor-base);
  padding: 5px 10px;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}
.delete-note-button {
  color: var(--v-whiteColor-base);
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--v-mainColor-base);
  padding: 5px 10px;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
}
</style>
